import marketingSiteRouteMap from '@/constants/marketingSiteRouteMap';
import { routeMap } from '@/routes/routeMap';
import { environment } from '@/utils/env.utils';
import { assertCondition } from '@/utils/typescript';

/* eslint-disable no-restricted-properties */
const Configs = [
  {
    country: 'SE',
    isoCountryCode: 'SE',
    currency: 'SEK',
    locale: 'sv-SE',
    callingCode: '+46',
    theme: 'default',
    customerServiceEmail: 'kundservice@fairlo.se',
    internalLinks: {
      loginSuccess: routeMap.login.SE.bankid,
    },
    externalLinks: {
      home: marketingSiteRouteMap.home,
      contact: marketingSiteRouteMap.contact,
      terms: marketingSiteRouteMap.termsAndConditions,
      about: marketingSiteRouteMap.aboutFairlo,
      privacyPolicy: marketingSiteRouteMap.integrity,
      faq: marketingSiteRouteMap.faq,
    },
  },
  {
    country: 'UK',
    // ISO 3166-1 alpha-2; "UK" is not a valid code (GB is).
    isoCountryCode: 'GB',
    currency: 'GBP',
    locale: 'en-GB',
    callingCode: '+44',
    theme: 'default',
    customerServiceEmail: 'hello@fairlo.co.uk',
    internalLinks: {
      loginSuccess: routeMap.login.UK.authenticate,
    },
    externalLinks: {
      home: marketingSiteRouteMap.home,
      contact: marketingSiteRouteMap.contact,
      terms: marketingSiteRouteMap.termsAndConditions,
      about: marketingSiteRouteMap.aboutFairlo,
      privacyPolicy: marketingSiteRouteMap.integrity,
      faq: marketingSiteRouteMap.faq,
    },
  },
  {
    country: 'FI',
    isoCountryCode: 'FI',
    currency: 'EUR',
    locale: 'fi-FI',
    callingCode: '+358',
    theme: 'fairlo-pro',
    customerServiceEmail: 'asiakaspalvelu@vauraus.fairlo.fi',
    internalLinks: {
      loginSuccess: routeMap.login.FI.success,
    },
    externalLinks: {
      home: 'https://vauraus.fi',
      contact: 'https://vauraus.fi/ota-yhteytta/',
      terms: undefined,
      about: undefined,
      privacyPolicy: undefined,
      faq: undefined,
    },
  },
] as const;
/* eslint-enable no-restricted-properties */

type Config = (typeof Configs)[number];
export type Country = Config['country'];
export type Currency = Config['currency'];
export type Locale = Config['locale'];
type ConfigFor<C extends Country> = Extract<Config, { country: C }>;

export const LOCALES = Configs.map(({ locale }) => locale);

export const getConfig = () => {
  // eslint-disable-next-line no-restricted-properties
  const config = Configs.find(({ country }) => country === environment.country);

  assertCondition(!!config, 'Config not found');

  return config;
};

export const getConfigByLocale = (locale: Locale) => {
  const config = Configs.find(({ locale: countryLocale }) => countryLocale === locale);

  assertCondition(!!config, 'Config not found');

  return config;
};

export const getConfigByCountry = <C extends Country>(country: C): ConfigFor<C> => {
  const config = Configs.find(({ country: countryConfigCountry }) => countryConfigCountry === country);

  assertCondition(!!config, 'Config not found');

  return config as ConfigFor<C>;
};

export const getConfigSE = () => {
  assertCondition(getConfig().country === 'SE', `Wrong country, expected SE, got ${getConfig().country}`);
  return getConfigByCountry('SE');
};
export const getConfigUK = () => {
  assertCondition(getConfig().country === 'UK', `Wrong country, expected UK, got ${getConfig().country}`);
  return getConfigByCountry('UK');
};
export const getConfigFI = () => {
  assertCondition(getConfig().country === 'FI', `Wrong country, expected FI, got ${getConfig().country}`);
  return getConfigByCountry('FI');
};
